(() => {
  const legacyDevice = new URL(window.location.href).searchParams.get('device');
  if (legacyDevice) {
    window.location.replace(`/pages/firmware/${encodeURIComponent(legacyDevice)}`);
    return;
  }

  const scriptAssetUrl = document.currentScript && document.currentScript.src;
  const assetUrl = (filename) => (
    scriptAssetUrl ? new URL(filename, new URL('.', scriptAssetUrl)).href : filename
  );

  const CATEGORY_ORDER = [
    ['all', 'All devices'],
    ['sensors', 'Sensors'],
    ['controls', 'Controls'],
    ['plugs', 'Smart plugs'],
    ['lighting', 'Lighting'],
    ['hubs', 'Hubs'],
    ['blinds', 'Blinds'],
    ['access', 'Access']
  ];

  const CATEGORY_DESCRIPTIONS = {
    sensors: 'Environmental, safety, and presence sensors',
    controls: 'Switches, buttons, and smart controllers',
    plugs: 'Smart plugs and power monitoring devices',
    lighting: 'Night lights, bulbs, and lighting devices',
    hubs: 'Bridges and smart home hubs',
    blinds: 'Smart blinds and window control',
    access: 'Garage and access devices'
  };

  const PROTOCOL_OVERRIDES = {
    'smart-keyboard-mk1': 'Matter',
    'smart-switch-mt1-thread': 'Thread',
    'smart-plug-m1-m2': 'Matter',
    'smart-color-night-light-matter': 'Matter',
    'smart-bridge-mz1': 'Matter / Zigbee',
    'smart-garage-door-opener-matter': 'Matter'
  };

  const normalize = (value) => (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const classify = (id, title) => {
    const value = normalize(`${id} ${title}`);
    if (value.includes('garage door opener')) return 'access';
    if (value.includes('blind')) return 'blinds';
    if (value.includes('bridge') || value.includes('hub')) return 'hubs';
    if (value.includes('plug')) return 'plugs';
    if (value.includes('night light') || value.includes('bulb')) return 'lighting';
    if (value.includes('switch') || value.includes('keyboard') || value.includes('button')) return 'controls';
    if (
      value.includes('sensor') ||
      value.includes('temperature') ||
      value.includes('humidity') ||
      value.includes('motion') ||
      value.includes('water leak') ||
      value.includes('soil') ||
      value.includes('presence')
    ) return 'sensors';
    return 'sensors';
  };

  const protocolFor = (id, title) => {
    if (PROTOCOL_OVERRIDES[id]) return PROTOCOL_OVERRIDES[id];
    const value = normalize(title);
    if (value.includes('matter')) return 'Matter';
    if (value.includes('thread')) return 'Thread';
    return 'Zigbee';
  };

  const neutralProtocolIcon = () => `
    <svg class="release-card__protocol-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="6" cy="7" r="2.25"></circle>
      <circle cx="18" cy="7" r="2.25"></circle>
      <circle cx="12" cy="17" r="2.25"></circle>
      <path d="M8 8.2l2.7 6M16 8.2l-2.7 6M8.4 7h7.2"></path>
    </svg>
  `;

  const licensedProtocolIcon = (name) => `
    <span class="release-card__protocol-mark release-card__protocol-mark--${name.toLowerCase()}" aria-hidden="true">
      <img
        class="release-card__protocol-logo release-card__protocol-logo--${name.toLowerCase()}"
        src="${assetUrl(`release-protocol-${name.toLowerCase()}-icon.png`)}"
        alt=""
        loading="lazy"
        decoding="async"
      >
    </span>
  `;

  const licensedProtocolLogo = (name) => `${licensedProtocolIcon(name)}<span>${name}</span>`;

  const protocolMarkup = (protocol) => {
    if (protocol === 'Matter') return licensedProtocolLogo('Matter');
    if (protocol === 'Thread') return licensedProtocolLogo('Thread');
    if (protocol === 'Matter / Zigbee') {
      return `
        ${licensedProtocolIcon('Matter')}
        <span class="release-card__protocol-divider" aria-hidden="true">+</span>
        ${neutralProtocolIcon()}
        <span>Zigbee</span>
      `;
    }
    return `${neutralProtocolIcon()}<span>${protocol}</span>`;
  };

  const displayDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value || '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const updateUrl = (deviceId) => (
    deviceId ? `/pages/firmware/${encodeURIComponent(deviceId)}` : '/pages/release-notes'
  );

  const initializeDirectory = () => {
    const archive = document.querySelector('.thirdreality-release-archive');
    if (!archive || archive.dataset.releaseDirectoryReady === 'true') return;

    const grid = archive.querySelector('.thirdreality-release-archive__grid');
    const cards = grid ? Array.from(grid.querySelectorAll('.thirdreality-release-card')) : [];
    if (!grid || !cards.length) return;

    archive.dataset.releaseDirectoryReady = 'true';
    archive.classList.add('is-enhanced');
    archive.setAttribute('aria-busy', 'false');

    const categoryLabels = new Map(CATEGORY_ORDER);
    let latestDate = null;

    cards.forEach((card) => {
      const id = card.id;
      const heading = card.querySelector('.thirdreality-release-card__header h3');
      const dateElement = card.querySelector('.thirdreality-release-card__date');
      const summary = card.querySelector('.thirdreality-release-card__summary');
      const versionElement = card.querySelector('.thirdreality-release-card__summary-version');
      const title = heading ? heading.textContent.trim() : id;
      const category = card.dataset.releaseFamily || classify(id, title);
      const protocol = card.dataset.releaseProtocol || protocolFor(id, title);
      const dateText = dateElement ? dateElement.textContent.trim() : '';
      const parsedDate = dateText ? new Date(dateText) : null;

      if (parsedDate && !Number.isNaN(parsedDate.getTime()) && (!latestDate || parsedDate > latestDate)) {
        latestDate = parsedDate;
      }

      card.dataset.releaseFamily = category;
      card.dataset.releaseSearch = normalize(`${title} ${id} ${protocol} ${versionElement ? versionElement.textContent : ''}`);

      if (heading) {
        const link = document.createElement('a');
        link.href = updateUrl(id);
        link.textContent = title;
        heading.textContent = '';
        heading.appendChild(link);
      }

      const family = document.createElement('p');
      family.className = 'release-card__family';
      family.textContent = categoryLabels.get(category) || 'Devices';
      const header = card.querySelector('.thirdreality-release-card__header');
      if (header) header.prepend(family);

      let protocolBadge = null;
      if (protocol) {
        protocolBadge = document.createElement('span');
        protocolBadge.className = 'release-card__protocol';
        protocolBadge.classList.toggle('release-card__protocol--licensed', protocol !== 'Zigbee');
        protocolBadge.classList.toggle('release-card__protocol--mixed', protocol.includes(' / '));
        protocolBadge.setAttribute('aria-label', protocol.replace(' / ', ' and '));
        protocolBadge.innerHTML = protocolMarkup(protocol);
      }

      if (summary) {
        const candidates = Array.from(summary.querySelectorAll('p')).filter((node) => (
          !node.classList.contains('thirdreality-release-card__summary-label') &&
          !node.classList.contains('thirdreality-release-card__summary-version') &&
          !node.classList.contains('thirdreality-release-card__summary-note')
        ));
        const excerpt = candidates[0];
        if (excerpt) excerpt.classList.add('release-card__excerpt');
      }

      const footer = document.createElement('div');
      footer.className = 'release-card__footer';
      if (protocolBadge) footer.appendChild(protocolBadge);

      const detailLink = document.createElement('a');
      detailLink.className = 'release-card__link';
      detailLink.href = updateUrl(id);
      detailLink.textContent = 'View release notes';
      footer.appendChild(detailLink);
      card.appendChild(footer);
    });

    const overview = document.createElement('div');
    overview.className = 'release-directory__overview';
    overview.innerHTML = `
      <p class="release-directory__overview-copy">
        Find firmware versions, improvements, fixes, and compatibility notes for your Third Reality device.
      </p>
      <div class="release-directory__overview-meta" aria-label="Release note library summary">
        <span class="release-directory__stat"><strong>${cards.length}</strong><span>devices listed</span></span>
        <span class="release-directory__stat"><strong>${latestDate ? displayDate(latestDate).replace(/, \d{4}$/, '') : 'Current'}</strong><span>latest update</span></span>
      </div>
    `;

    const controls = document.createElement('div');
    controls.className = 'release-directory__controls';
    controls.innerHTML = `
      <div class="release-directory__search-wrap">
        <label class="visually-hidden" for="ReleaseDirectorySearch">Search release notes</label>
        <input class="release-directory__search" id="ReleaseDirectorySearch" type="search" placeholder="Search product or model" autocomplete="off">
      </div>
    `;

    const results = document.createElement('div');
    results.className = 'release-directory__results';
    results.innerHTML = '<span aria-live="polite"></span><button class="release-directory__clear" type="button" hidden>Clear filters</button>';

    const empty = document.createElement('div');
    empty.className = 'release-directory__empty';
    empty.hidden = true;
    empty.innerHTML = '<h3>No matching device</h3><p>Try a shorter model name or choose another category.</p>';
    const groups = [];
    CATEGORY_ORDER.filter(([value]) => value !== 'all').forEach(([value, label]) => {
      const groupCards = cards
        .filter((card) => card.dataset.releaseFamily === value)
        .sort((left, right) => {
          const leftTitle = left.querySelector('h3')?.textContent.trim() || '';
          const rightTitle = right.querySelector('h3')?.textContent.trim() || '';
          return leftTitle.localeCompare(rightTitle, 'en', { numeric: true });
        });
      if (!groupCards.length) return;

      const group = document.createElement('section');
      group.className = 'release-directory__group';
      group.dataset.releaseGroup = value;
      const gridId = `ReleaseDirectoryGroup-${value}`;
      group.innerHTML = `
        <button class="release-directory__group-toggle" type="button" aria-expanded="false" aria-controls="${gridId}">
          <div>
            <h2 class="release-directory__group-title">${label}</h2>
            <p class="release-directory__group-description">${CATEGORY_DESCRIPTIONS[value] || 'Device firmware history'}</p>
          </div>
          <span class="release-directory__group-count"></span>
          <span class="release-directory__group-chevron" aria-hidden="true"></span>
        </button>
        <div class="release-directory__group-grid" id="${gridId}" hidden></div>
      `;
      const groupGrid = group.querySelector('.release-directory__group-grid');
      groupCards.forEach((card) => groupGrid.appendChild(card));
      grid.appendChild(group);
      groups.push(group);
    });
    grid.appendChild(empty);

    archive.prepend(results);
    archive.prepend(controls);
    archive.prepend(overview);

    const search = controls.querySelector('.release-directory__search');
    const resultLabel = results.querySelector('span');
    const clearButton = results.querySelector('.release-directory__clear');

    const setGroupExpanded = (group, expanded) => {
      const toggle = group.querySelector('.release-directory__group-toggle');
      const groupGrid = group.querySelector('.release-directory__group-grid');
      group.classList.toggle('is-expanded', expanded);
      if (toggle) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (groupGrid) groupGrid.hidden = !expanded;
    };

    const applyFilters = () => {
      const query = normalize(search.value);
      let visible = 0;
      cards.forEach((card) => {
        const searchMatches = !query || card.dataset.releaseSearch.includes(query);
        const show = searchMatches;
        card.hidden = !show;
        if (show) visible += 1;
      });
      groups.forEach((group) => {
        const groupCards = Array.from(group.querySelectorAll('.thirdreality-release-card'));
        const visibleInGroup = groupCards.filter((card) => !card.hidden).length;
        group.hidden = visibleInGroup === 0;
        const count = group.querySelector('.release-directory__group-count');
        if (count) count.textContent = `${visibleInGroup} ${visibleInGroup === 1 ? 'device' : 'devices'}`;
        if (query) setGroupExpanded(group, visibleInGroup > 0);
      });
      resultLabel.textContent = query
        ? `${visible} matching ${visible === 1 ? 'device' : 'devices'}`
        : `${groups.length} product categories`;
      clearButton.hidden = !query;
      empty.hidden = visible !== 0;
    };

    grid.addEventListener('click', (event) => {
      const toggle = event.target.closest('.release-directory__group-toggle');
      if (!toggle) return;
      const selectedGroup = toggle.closest('.release-directory__group');
      const expand = toggle.getAttribute('aria-expanded') !== 'true';
      setGroupExpanded(selectedGroup, expand);
    });

    search.addEventListener('input', applyFilters);
    clearButton.addEventListener('click', () => {
      search.value = '';
      applyFilters();
      search.focus();
    });

    applyFilters();
  };

  initializeDirectory();
})();
